import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

export enum ButtonAppearance {
  regular = 'regular',
  muted = 'muted',
  mutedWithActive = 'mutedWithActive', // active state only useful for UiButton::LinkTo
  orange = 'orange',
  red = 'red',
  black = 'black',
  green = 'green',
  blue = 'blue',
  specialBlue = 'special-blue',
  specialPurple = 'special-purple',
  specialRed = 'special-red',
  specialGreen = 'special-green',
}

// disabled styles are the same for all themes
const disabledClasses =
  'disabled:cursor-default disabled:pointer-events-none disabled:border-dusty-black-100 disabled:bg-dusty-black-50 disabled:text-dusty-black-200'

const outlineClasses = 'outline-solid outline-2 outline-transparent'

// border, background and text colours
const buttonThemes: Partial<Record<ThemeKeys, string[]>> = {
  [ThemeKeys.blue]: [
    'border border-oceany-blue-300 hover:border-oceany-blue-400 focus:border-oceany-blue-400',
    'bg-oceany-blue-300 hover:bg-oceany-blue-350 focus:bg-oceany-blue-350',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.green]: [
    'border border-ms-green-300 hover:border-ms-green-400 focus:border-ms-green-400',
    'bg-ms-green-300 hover:bg-ms-green-350 focus:bg-ms-green-350',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.darkBlue]: [
    'border border-wl-blue-300 hover:border-wl-blue-250 focus:border-wl-blue-250',
    'bg-wl-blue-300 hover:bg-wl-blue-500 focus:bg-wl-blue-500',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.orange]: [
    'border border-juicy-orange-500',
    'bg-juicy-orange-300 hover:bg-juicy-orange-350 focus:bg-juicy-orange-350',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.red]: [
    'border border-watermelony-red-400',
    'bg-watermelony-red-300 hover:bg-watermelony-red-300 focus:bg-watermelony-red-300',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.black]: [
    'border border-dusty-black-400',
    'bg-dusty-black-300 hover:bg-dusty-black-300 focus:bg-dusty-black-300',
    'text-white hover:text-dusty-black-50 focus:text-dusty-black-50',
    disabledClasses,
  ],
  [ThemeKeys.specialBlue]: [
    'border-none rounded-full!',
    'bg-linear-to-br from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700 group-hover:from-sky-700 group-hover:to-indigo-700 focus:from-sky-700 focus:to-indigo-700',
    'text-white hover:text-white focus:text-white',
    disabledClasses,
  ],
  [ThemeKeys.specialPurple]: [
    'border-none rounded-full!',
    'bg-linear-to-br from-purple-600 to-violet-800 hover:from-purple-700 hover:to-violet-900 group-hover:from-purple-700 group-hover:to-violet-900 focus:from-purple-700 focus:to-violet-900',
    'text-white hover:text-white focus:text-white',
    disabledClasses,
  ],
  [ThemeKeys.specialRed]: [
    'border-none rounded-full!',
    'bg-linear-to-br from-red-500 to-pink-700 hover:from-red-600 hover:to-pink-800 group-hover:from-red-600 group-hover:to-pink-800 focus:from-red-600 focus:to-pink-800',
    'text-white hover:text-white focus:text-white',
    disabledClasses,
  ],
  [ThemeKeys.specialGreen]: [
    'border-none rounded-full!',
    'bg-linear-to-br from-green-700 to-emerald-700 hover:from-green-800 hover:to-emerald-800 group-hover:from-green-800 group-hover:to-emerald-800 focus:from-green-800 focus:to-emerald-800',
    'text-white hover:text-white focus:text-white',
    disabledClasses,
  ],
  [ThemeKeys.default]: [
    'border border-dusty-black-100',
    'bg-white hover:bg-dusty-black-50 focus:bg-dusty-black-50',
    'text-black hover:text-black focus:text-black', // including hover/focus here to override default link styling when used with <a> tags
    disabledClasses,
  ],
}

/**
 * This function uses a button appearance enum and optional subscription type, to return an array of classes that can
 * be used to theme the UiButton variety of components.
 */
export function appearanceToThemeClasses(appearance: ButtonAppearance, subscriptionType?: SubscriptionType): string[] {
  switch (appearance) {
    case ButtonAppearance.regular:
      // regular appearance sets its theme based on the current subscription type
      return [...getSubscriptionTypeBaseTheme(subscriptionType), ...getSubscriptionTypeOutline(subscriptionType)]
    case ButtonAppearance.muted:
      // applies outline colour based on subscription type
      return [...getBaseTheme(ThemeKeys.default), ...getSubscriptionTypeOutline(subscriptionType)]
    case ButtonAppearance.mutedWithActive:
      // applies outline colour AND active colour based on subscription type
      return [
        ...getBaseTheme(ThemeKeys.default),
        ...getSubscriptionTypeOutline(subscriptionType),
        ...getSubscriptionTypeActiveLink(subscriptionType),
      ]
    case ButtonAppearance.blue:
      return [...getBaseTheme(ThemeKeys.blue), ...getThemedOutline(ThemeKeys.blue)]
    case ButtonAppearance.green:
      return [...getBaseTheme(ThemeKeys.green), ...getThemedOutline(ThemeKeys.green)]
    case ButtonAppearance.orange:
      return [...getBaseTheme(ThemeKeys.orange), ...getThemedOutline(ThemeKeys.orange)]
    case ButtonAppearance.red:
      return [...getBaseTheme(ThemeKeys.red), ...getThemedOutline(ThemeKeys.red)]
    case ButtonAppearance.black:
      return [...getBaseTheme(ThemeKeys.black), ...getThemedOutline(ThemeKeys.black)]
    case ButtonAppearance.specialBlue:
      return [...getBaseTheme(ThemeKeys.specialBlue), ...getThemedOutline(ThemeKeys.specialBlue)]
    case ButtonAppearance.specialPurple:
      return [...getBaseTheme(ThemeKeys.specialPurple), ...getThemedOutline(ThemeKeys.specialPurple)]
    case ButtonAppearance.specialRed:
      return [...getBaseTheme(ThemeKeys.specialRed), ...getThemedOutline(ThemeKeys.specialRed)]
    case ButtonAppearance.specialGreen:
      return [...getBaseTheme(ThemeKeys.specialGreen), ...getThemedOutline(ThemeKeys.specialGreen)]
    default:
      return [...getBaseTheme(ThemeKeys.default), ...getThemedOutline(ThemeKeys.default)]
  }
}

function getBaseTheme(themeKey: ThemeKeys): string[] {
  return buttonThemes[themeKey] ?? []
}

function getSubscriptionTypeBaseTheme(subscriptionType?: SubscriptionType): string[] {
  switch (subscriptionType) {
    case SubscriptionType.Reading:
      return getBaseTheme(ThemeKeys.blue)
    case SubscriptionType.Maths:
      return getBaseTheme(ThemeKeys.green)
    case SubscriptionType.Writing:
      return getBaseTheme(ThemeKeys.darkBlue)
    default: // a subscriptionType was not given, use a neutral colour
      return getBaseTheme(ThemeKeys.black)
  }
}

function getSubscriptionTypeOutline(subscriptionType?: SubscriptionType): string[] {
  switch (subscriptionType) {
    case SubscriptionType.Reading:
      return getThemedOutline(ThemeKeys.blue)
    case SubscriptionType.Maths:
      return getThemedOutline(ThemeKeys.green)
    case SubscriptionType.Writing:
      return getThemedOutline(ThemeKeys.darkBlue)
    default: // a subscriptionType was not given, use a neutral colour
      return getThemedOutline(ThemeKeys.black)
  }
}

function getThemedOutline(themeKey: ThemeKeys): string[] {
  switch (themeKey) {
    case ThemeKeys.blue:
      return [outlineClasses, 'focus:outline-oceany-blue-300/75']
    case ThemeKeys.green:
      return [outlineClasses, 'focus:outline-ms-green-300/75']
    case ThemeKeys.darkBlue:
      return [outlineClasses, 'focus:outline-wl-blue-300/75']
    case ThemeKeys.orange:
      return [outlineClasses, 'focus:outline-juicy-orange-300/75']
    case ThemeKeys.red:
      return [outlineClasses, 'focus:outline-watermelony-red-300/75']
    case ThemeKeys.specialBlue:
    case ThemeKeys.specialPurple:
    case ThemeKeys.specialRed:
    case ThemeKeys.specialGreen:
      return [outlineClasses, 'focus:outline-blue-325 outline-offset-2']
    case ThemeKeys.black:
    case ThemeKeys.default:
    default:
      return [outlineClasses, 'focus:outline-dusty-black-300/75']
  }
}

function getSubscriptionTypeActiveLink(subscriptionType?: SubscriptionType): string[] {
  switch (subscriptionType) {
    case SubscriptionType.Reading:
      return getThemedActiveLink(ThemeKeys.blue)
    case SubscriptionType.Maths:
      return getThemedActiveLink(ThemeKeys.green)
    case SubscriptionType.Writing:
      return getThemedActiveLink(ThemeKeys.darkBlue)
    default: // a subscriptionType was not given, use a neutral colour
      return getThemedActiveLink(ThemeKeys.black)
  }
}

/**
 * Necessary for LinkTo's when on the current route. active-link:* is a custom tailwind variant, applied when the
 * element has .active class.
 *
 * Similar to the base themes above, but no need for hover / focus states. They are separate, because some appearances
 * mix and match theme colours in a similar way to outline (eg muted when inactive, regular when active..)
 */
function getThemedActiveLink(themeKey: ThemeKeys): string[] {
  switch (themeKey) {
    case ThemeKeys.blue:
      return ['active-link:border-oceany-blue-350', 'active-link:bg-oceany-blue-350', 'active-link:text-dusty-black-50']
    case ThemeKeys.green:
      return ['active-link:border-ms-green-400', 'active-link:bg-ms-green-350', 'active-link:text-dusty-black-50']
    case ThemeKeys.darkBlue:
      return ['active-link:border-wl-blue-250', 'active-link:bg-wl-blue-500', 'active-link:text-dusty-black-50']
    case ThemeKeys.orange:
    case ThemeKeys.red:
    case ThemeKeys.black:
    case ThemeKeys.default:
    default:
      return ['active-link:border-dusty-black-400', 'active-link:bg-dusty-black-300', 'active-link:text-dusty-black-50']
  }
}
